import React, { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { LazyMotion, domAnimation } from "framer-motion";

import { AuthProvider } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";

// Public pages — lazy loaded so they ship in separate chunks.
// Home è importata in modo eager: è la landing più visitata e, servita prerenderizzata,
// il fallback di Suspense al primo mount svuoterebbe per un attimo il contenuto già visibile.
import Home from "./pages/Home";
const About = lazy(() => import("./pages/About"));
const Storia = lazy(() => import("./pages/Storia"));
const Services = lazy(() => import("./pages/Services"));
const Listino = lazy(() => import("./pages/Listino"));
const Staff = lazy(() => import("./pages/Staff"));
const StaffDetail = lazy(() => import("./pages/StaffDetail"));
const Gallery = lazy(() => import("./pages/Gallery"));
const News = lazy(() => import("./pages/News"));
const NewsDetail = lazy(() => import("./pages/NewsDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const Booking = lazy(() => import("./pages/Booking"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin pages — entirely separate chunk (never loaded for public visitors)
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const ServicesManager = lazy(() => import("./pages/admin/ServicesManager"));
const StaffManager = lazy(() => import("./pages/admin/StaffManager"));
const GalleryManager = lazy(() => import("./pages/admin/GalleryManager"));
const NewsManager = lazy(() => import("./pages/admin/NewsManager"));
const BookingsManager = lazy(() => import("./pages/admin/BookingsManager"));
const PageContentManager = lazy(() => import("./pages/admin/PageContentManager"));
const GoogleMapsPoster = lazy(() => import("./pages/admin/GoogleMapsPoster"));
const PricesManager = lazy(() => import("./pages/admin/PricesManager"));
const ContactManager = lazy(() => import("./pages/admin/ContactManager"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Google Analytics 4 (G-GZ583HTXVZ) — invia una page_view a ogni cambio di
// route. Il tag in public/index.html è configurato con send_page_view:false,
// così qui gestiamo noi le pagine viste ed evitiamo conteggi doppi.
function GATracker() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: pathname + search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, search]);
  return null;
}

function RouteFallback() {
  return (
    <div
      data-testid="route-fallback"
      className="min-h-[60vh] flex items-center justify-center bg-[#0a0a0a]"
      aria-label="Caricamento in corso"
    >
      <div className="w-10 h-10 border-2 border-[#C9A961]/30 border-t-[#C9A961] rounded-full animate-spin" />
    </div>
  );
}

function PublicShell({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#050505]">
      <Navbar />
      <main className="flex-1">
        <Suspense fallback={<RouteFallback />}>{children}</Suspense>
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <LazyMotion features={domAnimation} strict>
          <AuthProvider>
            <BrowserRouter>
              <ScrollToTop />
              <GATracker />
              <CookieConsent />
              <Toaster theme="dark" position="top-right" richColors />
            <Routes>
              {/* Admin */}
              <Route
                path="/admin/login"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <AdminLogin />
                  </Suspense>
                }
              />
              <Route
                path="/admin"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <AdminLayout />
                  </Suspense>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="servizi" element={<ServicesManager />} />
                <Route path="staff" element={<StaffManager />} />
                <Route path="galleria" element={<GalleryManager />} />
                <Route path="news" element={<NewsManager />} />
                <Route path="prenotazioni" element={<BookingsManager />} />
                <Route path="contenuti" element={<PageContentManager />} />
                <Route path="listino" element={<PricesManager />} />
                <Route path="contatti" element={<ContactManager />} />
                <Route path="google-maps" element={<GoogleMapsPoster />} />
              </Route>

              {/* Public */}
              <Route path="/" element={<PublicShell><Home /></PublicShell>} />
              <Route path="/chi-siamo" element={<PublicShell><About /></PublicShell>} />
              <Route path="/chi-siamo/storia" element={<PublicShell><Storia /></PublicShell>} />
              <Route path="/servizi" element={<PublicShell><Services /></PublicShell>} />
              <Route path="/servizi/listino" element={<PublicShell><Listino /></PublicShell>} />
              <Route path="/staff" element={<PublicShell><Staff /></PublicShell>} />
              <Route path="/staff/:slug" element={<PublicShell><StaffDetail /></PublicShell>} />
              <Route path="/galleria" element={<PublicShell><Gallery /></PublicShell>} />
              <Route path="/news" element={<PublicShell><News /></PublicShell>} />
              <Route path="/news/:slug" element={<PublicShell><NewsDetail /></PublicShell>} />
              <Route path="/contatti" element={<PublicShell><Contact /></PublicShell>} />
              <Route path="/prenotazioni" element={<PublicShell><Booking /></PublicShell>} />
              <Route path="/cookie-policy" element={<PublicShell><CookiePolicy /></PublicShell>} />
              <Route path="/privacy-policy" element={<PublicShell><PrivacyPolicy /></PublicShell>} />
              <Route path="*" element={<PublicShell><NotFound /></PublicShell>} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </LazyMotion>
    </HelmetProvider>
    </div>
  );
}
